import { useState, useEffect, useRef, useCallback } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  getFederationInfo,
  createFederationAffiliation,
  renewFederationAffiliation,
  createFederationPixPayment,
  getFederationAffiliationStatus,
  verifyFederationAffiliation,
  FederationInfo,
  PixPaymentResult,
  FormConfigField,
} from "../../../services/federationApi";
import { Checkbox, FormControlLabel } from "@mui/material";
import PhotoUpload from "../../atoms/photoUpload";
import DocumentUpload from "../../atoms/documentUpload";
import {
  Container,
  Title,
  Subtitle,
  ResultBox,
  ResultContent,
  ResultTitle,
  ResultDetail,
} from "../federationVerification/styles";

type AffiliationStep = "form" | "payment" | "pending" | "active";

interface FederationAffiliationBoxProps {
  federationSlug: string;
  federationName: string;
  token: string | null;
  // Dados do atleta para pré-preencher
  athleteName: string;
  athleteCpf: string;
  athleteEmail: string;
  athletePhone?: string;
  athleteBirthDate?: string;
  athleteBelt?: string;
  athleteGym?: string;
  fighterId?: number;
  // Status existente (se já tem filiação)
  existingAffiliationId?: string;
  existingStatus?: string;
  // Renewal mode: when renewing an expired affiliation
  isRenewal?: boolean;
  // Callbacks
  onAffiliationActive?: (cpf?: string) => void;
  onCpfChanged?: (newCpf: string) => Promise<void>;
  // Atleta dependente (fighter gerenciado pelo dono, não o próprio dono)
  isDependentAthlete?: boolean;
  // Dados do dono da conta (responsável para menores e dependentes)
  accountName?: string;
  accountCpf?: string;
  accountPhone?: string;
  accountEmail?: string;
}

const parseBirthDate = (dateStr: string): Date | null => {
  // Tentar ISO (yyyy-mm-dd ou yyyy-mm-ddTHH:mm:ss)
  let d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d;
  // Tentar dd/mm/yyyy ou dd-mm-yyyy
  const parts = dateStr.replace(/\//g, "-").split("-");
  if (parts.length === 3 && parts[0].length <= 2) {
    d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
};

const isMinorByBirthDate = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  const birth = parseBirthDate(dateStr);
  if (!birth) return false;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age < 18;
};

const isValidCpf = (cpf: string): boolean => {
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let rem = (sum * 10) % 11;
  if (rem === 10) rem = 0;
  if (rem !== parseInt(digits[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  rem = (sum * 10) % 11;
  if (rem === 10) rem = 0;
  if (rem !== parseInt(digits[10])) return false;
  return true;
};

const formatCurrency = (cents: number): string => {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

/** Calculate age from birth date string */
const calculateAge = (dateStr: string): number | null => {
  const birth = parseBirthDate(dateStr);
  if (!birth) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

/** Check if athlete is eligible for a pricing plan */
const checkPlanEligibility = (
  plan: FederationInfo["pricingTables"][0],
  birthDate?: string,
  belt?: string,
  minor?: boolean,
): boolean => {
  const isMinor = minor || (birthDate ? isMinorByBirthDate(birthDate) : false);

  // Category check
  if (plan.category === "AFFILIATION_MINOR" && !isMinor) return false;
  if (plan.category === "AFFILIATION_ADULT" && isMinor) return false;

  // Custom eligibility rules
  const elig = plan.eligibility;
  if (!elig) return true;

  // Age range
  if (birthDate && (elig.minAge !== undefined || elig.maxAge !== undefined)) {
    const age = calculateAge(birthDate);
    if (age !== null) {
      if (elig.minAge !== undefined && age < elig.minAge) return false;
      if (elig.maxAge !== undefined && age > elig.maxAge) return false;
    }
  }

  // Belt check
  if (elig.belts && elig.belts.length > 0) {
    if (!belt) return false;
    const normalizedBelt = belt.toLowerCase().trim();
    const matches = elig.belts.some((b) => b.toLowerCase().trim() === normalizedBelt);
    if (!matches) return false;
  }

  return true;
};

const FederationAffiliationBox = ({
  federationSlug,
  federationName,
  token,
  athleteName,
  athleteCpf,
  athleteEmail,
  athletePhone,
  athleteBirthDate,
  athleteBelt,
  athleteGym,
  fighterId,
  existingAffiliationId,
  existingStatus,
  isRenewal = false,
  onAffiliationActive,
  onCpfChanged,
  isDependentAthlete = false,
  accountName,
  accountCpf,
  accountPhone,
  accountEmail,
}: FederationAffiliationBoxProps) => {
  // Determinar step inicial baseado no status existente
  const getInitialStep = (): AffiliationStep => {
    if (!existingStatus) return "form";
    if (existingStatus === "AWAITING_PAYMENT") return "payment";
    if (existingStatus === "PENDING") return "pending";
    if (existingStatus === "ACTIVE") return "active";
    return "form";
  };

  const [step, setStep] = useState<AffiliationStep>(getInitialStep);
  const [fedInfo, setFedInfo] = useState<FederationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form
  const [cpfInput, setCpfInput] = useState(athleteCpf || "");
  const [ownerCpfInput, setOwnerCpfInput] = useState(accountCpf || "");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [academyId, setAcademyId] = useState("");
  const [pricingTableId, setPricingTableId] = useState("");
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});
  const [photoUrl, setPhotoUrl] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");

  // Detecção de menor
  const athleteIsMinor = isMinorByBirthDate(athleteBirthDate);
  // CPF is always required for minors (requireMinorCpf config removed)

  // Verificar se responsável/atleta já tem conta na federação
  const [ownerHasAccount, setOwnerHasAccount] = useState(false);

  // Payment
  const [affiliationId, setAffiliationId] = useState(existingAffiliationId || "");
  const [pixData, setPixData] = useState<PixPaymentResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Limite de regeneração PIX (max 3)
  const pixRegenerateCountRef = useRef(0);
  const MAX_PIX_REGENERATIONS = 3;
  const [pixRegenerationExhausted, setPixRegenerationExhausted] = useState(false);

  // Polling
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Verificar se o responsável (ou o próprio atleta se independente) já tem conta
  useEffect(() => {
    // Usar CPF do atleta para a busca (obrigatório no /verify),
    // mas passar email do responsável/atleta para check de User existente
    const cpfForVerify = athleteCpf;
    const emailToCheck = (athleteIsMinor || isDependentAthlete)
      ? (accountEmail || athleteEmail)
      : athleteEmail;

    if (!cpfForVerify || cpfForVerify.replace(/\D/g, "").length !== 11) return;

    (async () => {
      try {
        const res = await verifyFederationAffiliation(
          federationSlug,
          cpfForVerify,
          undefined,
          token,
          true,
          emailToCheck || undefined
        );
        // Se tem filiação OU hasAccount (guardian por CPF ou user por email)
        if (res.affiliationId || res.hasAccount) {
          setOwnerHasAccount(true);
        }
      } catch {
        // Silencioso — se falhar, mostra o campo de senha normalmente
      }
    })();
  }, [federationSlug, athleteCpf, accountEmail, athleteEmail, isDependentAthlete, athleteIsMinor, token]);

  // Carregar info da federação
  useEffect(() => {
    let isCancelled = false;

    (async () => {
      try {
        const info = await getFederationInfo(federationSlug, token);
        if (isCancelled) return;
        setFedInfo(info);

        // Auto-selecionar academia se o nome bater
        if (athleteGym && info.academies.length > 0) {
          const normalize = (s: string) => s.trim().toLowerCase();
          const match = info.academies.find(
            (a) => normalize(a.name) === normalize(athleteGym || "")
          );
          if (match) setAcademyId(match.id);
        }

        // Auto-selecionar plano se só tiver um elegível
        // In renewal mode, prefer CREDENTIAL_RENEWAL plans
        const eligible = info.pricingTables.filter((p) => {
          if (isRenewal) {
            // Show CREDENTIAL_RENEWAL plans, or fallback to affiliation plans
            return p.category === "CREDENTIAL_RENEWAL" ||
              checkPlanEligibility(p, athleteBirthDate, athleteBelt, isMinorByBirthDate(athleteBirthDate));
          }
          return checkPlanEligibility(p, athleteBirthDate, athleteBelt, isMinorByBirthDate(athleteBirthDate));
        });
        if (isRenewal) {
          // Prefer CREDENTIAL_RENEWAL if available
          const renewalPlans = eligible.filter((p) => p.category === "CREDENTIAL_RENEWAL");
          if (renewalPlans.length === 1) {
            setPricingTableId(renewalPlans[0].id);
          } else if (eligible.length === 1) {
            setPricingTableId(eligible[0].id);
          }
        } else if (eligible.length === 1) {
          setPricingTableId(eligible[0].id);
        }

        // Auto-preencher campos dinâmicos com dados já existentes do atleta
        if (athleteBelt && info.formConfig) {
          const allFields = [
            ...(info.formConfig.extraBaseFields || []),
            ...(info.formConfig.customFields || []),
          ];
          const faixaField = allFields.find(
            (f) => f.key === "faixa" || f.key === "belt"
          );
          if (faixaField) {
            const beltValue = athleteBelt.toLowerCase();
            // Se é select, encontrar a option que bate
            if (faixaField.type === "select" && faixaField.options) {
              const match = faixaField.options.find(
                (o) => o.value.toLowerCase() === beltValue || o.label.toLowerCase() === beltValue
              );
              if (match) {
                setCustomFieldValues((prev) => ({ ...prev, [faixaField.key]: match.value }));
              }
            } else {
              setCustomFieldValues((prev) => ({ ...prev, [faixaField.key]: beltValue }));
            }
          }
        }
      } catch (err: any) {
        if (isCancelled) return;
        if (err?.response?.status === 401) {
          setError("Sessão expirada. Faça login novamente para continuar.");
          return;
        }
        setError("Erro ao carregar dados da federação.");
      }
    })();

    return () => { isCancelled = true; };
  }, [federationSlug, token]);

  // Se já tem affiliationId existente com AWAITING_PAYMENT, buscar PIX automaticamente
  useEffect(() => {
    if (existingAffiliationId && existingStatus === "AWAITING_PAYMENT" && fedInfo && !pixData) {
      setAffiliationId(existingAffiliationId);
      // Buscar PIX existente ou gerar novo
      handleGeneratePix(existingAffiliationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAffiliationId, existingStatus, fedInfo]);

  // Verificar se PIX expirou + contagem regressiva + auto-regenerar
  const [pixExpired, setPixExpired] = useState(false);
  const [pixCountdown, setPixCountdown] = useState("");
  const pixAutoRegeneratedRef = useRef(false);

  useEffect(() => {
    if (!pixData?.pixExpiresAt || step !== "payment") return;
    const expires = new Date(pixData.pixExpiresAt).getTime();

    const tick = () => {
      const remaining = expires - Date.now();
      if (remaining <= 0) {
        setPixExpired(true);
        setPixCountdown("");
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setPixCountdown(`${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [pixData?.pixExpiresAt, step]);

  // Auto-regenerar PIX 3 segundos após expirar (uma vez só, respeitando limite)
  useEffect(() => {
    if (!pixExpired || pixAutoRegeneratedRef.current || step !== "payment" || loading) return;
    if (pixRegenerateCountRef.current >= MAX_PIX_REGENERATIONS) return;
    const timeout = setTimeout(() => {
      if (!pixAutoRegeneratedRef.current) {
        pixAutoRegeneratedRef.current = true;
        setPixExpired(false);
        setPixData(null);
        handleGeneratePix();
      }
    }, 3000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixExpired, step, loading]);

  // Iniciar polling quando em step payment ou pending
  const pollingErrorCount = useRef(0);
  const [pollingStopped, setPollingStopped] = useState(false);

  const restartPolling = useCallback(() => {
    pollingErrorCount.current = 0;
    setPollingStopped(false);
    setError(null);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    if ((step === "payment" || step === "pending") && affiliationId && !pollingStopped) {
      // Não fazer polling se PIX expirou
      if (!(step === "payment" && pixExpired)) {
        const interval = step === "payment" ? 10000 : 30000;

        pollingRef.current = setInterval(async () => {
          if (isCancelled) return;
          try {
            const res = await getFederationAffiliationStatus(
              federationSlug,
              affiliationId,
              token
            );
            pollingErrorCount.current = 0;
            const newStatus = res.affiliation.status;

            if (newStatus === "ACTIVE") {
              setStep("active");
              onAffiliationActive?.(cpfInput.replace(/\D/g, ""));
            } else if (newStatus === "PENDING" && step === "payment") {
              setStep("pending");
            } else if (newStatus === "REJECTED") {
              setError(
                res.affiliation.rejectionReason ||
                  "Sua filiacao foi rejeitada pela federacao. Entre em contato para mais informacoes."
              );
              setStep("form");
            }
          } catch (err: any) {
            if (err?.response?.status === 401) {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              setPollingStopped(true);
              setError("Sessão expirada. Faça login novamente para continuar.");
              return;
            }
            pollingErrorCount.current += 1;
            if (pollingErrorCount.current >= 5) {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              setPollingStopped(true);
              setError("Erro ao verificar status. Clique abaixo para tentar novamente.");
            }
          }
        }, interval);
      }
    }

    return () => {
      isCancelled = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [step, affiliationId, federationSlug, token, pixExpired, pollingStopped]);

  const handleRenewAffiliation = async () => {
    if (!pricingTableId) {
      setError("Selecione um plano de renovação.");
      return;
    }
    if (!existingAffiliationId) {
      setError("Filiação não encontrada para renovação.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await renewFederationAffiliation(
        federationSlug,
        existingAffiliationId,
        pricingTableId,
        token
      );

      setAffiliationId(result.affiliationId);

      if (result.requiresPayment) {
        await handleGeneratePix(result.affiliationId);
      } else {
        setStep("pending");
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setError("Sessão expirada. Faça login novamente para continuar.");
      } else {
        const responseData = err?.response?.data;
        setError(
          responseData?.message || "Erro ao renovar filiação. Tente novamente."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAffiliation = async () => {
    const errors: string[] = [];

    // Validar CPF: sempre obrigatório (adultos e menores)
    const cpfDigits = cpfInput.replace(/\D/g, "");
    if (cpfDigits.length !== 11) {
      errors.push("CPF é obrigatório e deve ter 11 dígitos.");
    } else if (!isValidCpf(cpfDigits)) {
      errors.push("CPF inválido. Verifique os dígitos informados.");
    }

    // Validar guardian para menores e dependentes
    if ((athleteIsMinor || isDependentAthlete) && !ownerCpfInput.replace(/\D/g, "")) {
      errors.push("CPF do responsavel e obrigatorio.");
    }

    // Senha sincronizada automaticamente do Torneio Facil — sem validação

    if (!academyId && fedInfo && fedInfo.academies.length > 0) {
      errors.push("Selecione uma academia.");
    }
    if (!pricingTableId) {
      errors.push("Selecione um plano de filiação.");
    }

    // Validar campos dinâmicos obrigatórios
    if (fedInfo?.formConfig) {
      const allDynFields = [
        ...(fedInfo.formConfig.extraBaseFields || []),
        ...(fedInfo.formConfig.customFields || []),
      ];
      for (const field of allDynFields) {
        if (field.required) {
          // Foto e documento usam estados separados
          if (field.key === "photoUrl") {
            if (!photoUrl) errors.push(`${field.label} é obrigatório.`);
            continue;
          }
          if (field.key === "documentUrl") {
            if (!documentUrl) errors.push(`${field.label} é obrigatório.`);
            continue;
          }
          const val = customFieldValues[field.key];
          if (val === undefined || val === null || val === "") {
            errors.push(`${field.label} é obrigatório.`);
          }
        }
      }
    }

    if (errors.length > 0) {
      setError(errors.join(" "));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Montar customFields (sem foto/documento — vão como top-level)
      const customFields: Record<string, unknown> = { ...customFieldValues };
      if (athleteBelt) customFields.faixa = athleteBelt;

      const cpfToSend = cpfInput.replace(/\D/g, "");

      const result = await createFederationAffiliation(
        federationSlug,
        {
          cpf: cpfToSend,
          name: athleteName,
          email: athleteEmail,
          phone: athletePhone,
          birthDate: athleteBirthDate ? (parseBirthDate(athleteBirthDate)?.toISOString().split("T")[0] || athleteBirthDate) : undefined,
          academyId: academyId || undefined,
          pricingTableId,
          belt: athleteBelt,
          fighterId,
          photoUrl: photoUrl || undefined,
          documentUrl: documentUrl || undefined,
          customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
          isMinor: athleteIsMinor || undefined,
          guardianName: (athleteIsMinor || isDependentAthlete) ? accountName : undefined,
          guardianCpf: (athleteIsMinor || isDependentAthlete) ? ownerCpfInput.replace(/\D/g, "") : undefined,
          guardianPhone: (athleteIsMinor || isDependentAthlete) ? (accountPhone || athletePhone) : undefined,
          guardianEmail: (athleteIsMinor || isDependentAthlete) ? (accountEmail || athleteEmail || "") : undefined,
          // Senha sincronizada automaticamente pelo backend
        },
        token
      );

      setAffiliationId(result.affiliationId);

      // Se o CPF usado na filiacao e diferente do original, atualizar no fighter
      const usedCpf = cpfInput.replace(/\D/g, "");
      const originalCpf = (athleteCpf || "").replace(/\D/g, "");
      if (usedCpf && usedCpf !== originalCpf && onCpfChanged) {
        try {
          await onCpfChanged(usedCpf);
        } catch {
          // Continuar mesmo se falhar
        }
      }

      if (result.requiresPayment) {
        // Gerar PIX
        await handleGeneratePix(result.affiliationId);
      } else {
        setStep("pending");
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setError("Sessão expirada. Faça login novamente para continuar.");
        setLoading(false);
        return;
      }
      const responseData = err?.response?.data;
      if (err?.response?.status === 409 && responseData?.existingAffiliationId) {
        // Já tem filiação — atualizar CPF se necessário e redirecionar
        setAffiliationId(responseData.existingAffiliationId);

        // Atualizar CPF do fighter se mudou
        const usedCpf = cpfInput.replace(/\D/g, "");
        const originalCpf = (athleteCpf || "").replace(/\D/g, "");
        if (usedCpf && usedCpf !== originalCpf && onCpfChanged) {
          try { await onCpfChanged(usedCpf); } catch {}
        }

        if (responseData.existingStatus === "AWAITING_PAYMENT") {
          setError("Já existe uma filiação pendente de pagamento para este CPF. Gerando pagamento...");
          setTimeout(() => setError(null), 3000);
          await handleGeneratePix(responseData.existingAffiliationId);
        } else if (responseData.existingStatus === "PENDING") {
          setStep("pending");
        } else if (responseData.existingStatus === "ACTIVE") {
          setStep("active");
          onAffiliationActive?.(cpfInput.replace(/\D/g, ""));
        } else {
          setError(responseData.message || "Este CPF já possui filiação.");
        }
      } else {
        setError(
          responseData?.message || "Erro ao criar filiação. Tente novamente."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePix = async (affId?: string, overridePricingId?: string) => {
    const id = affId || affiliationId;
    const pricing = overridePricingId || pricingTableId;

    // Verificar limite de regenerações (não conta a primeira geração)
    if (pixRegenerateCountRef.current >= MAX_PIX_REGENERATIONS) {
      setPixRegenerationExhausted(true);
      setError(
        "Limite de tentativas de pagamento atingido. " +
        "Entre em contato com a federação para assistência."
      );
      return;
    }

    // Se não tem pricingTableId, buscar o status da filiação para ver se já tem PIX ativo
    if (!pricing) {
      setLoading(true);
      setError(null);
      try {
        const statusRes = await getFederationAffiliationStatus(federationSlug, id, token);
        const payments = statusRes.affiliation.payments || [];
        const activePix = payments.find(
          (p) => p.pixQrCode && p.status !== "EXPIRED" && p.status !== "FAILED"
            && p.pixExpiresAt && new Date(p.pixExpiresAt) > new Date()
        );
        if (activePix) {
          setPixData({
            paymentId: activePix.id,
            amount: activePix.amount,
            pixQrCode: activePix.pixQrCode || "",
            pixQrCodeImage: activePix.pixQrCodeImage || "",
            pixExpiresAt: activePix.pixExpiresAt || "",
            gatewayOrderId: "",
          });
          setStep("payment");
          return;
        }
        // Sem PIX ativo e sem pricingTableId — tentar auto-selecionar plano
        if (fedInfo && fedInfo.pricingTables.length > 0) {
          const firstPlan = fedInfo.pricingTables[0];
          setPricingTableId(firstPlan.id);
          // Gerar PIX com o plano selecionado
          setLoading(false);
          await handleGeneratePix(id, firstPlan.id);
          return;
        }
        setStep("form");
        setError("Selecione um plano para gerar o pagamento.");
      } catch {
        setError("Erro ao consultar status da filiação.");
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const pix = await createFederationPixPayment(
        federationSlug,
        id,
        pricing,
        token
      );
      setPixData(pix);
      setPixExpired(false);
      pixAutoRegeneratedRef.current = false; // Permitir auto-regeneração se este PIX também expirar
      // Incrementar contador de regenerações (após primeira geração bem-sucedida)
      if (step === "payment") {
        pixRegenerateCountRef.current += 1;
      }
      setStep("payment");
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setError("Sessão expirada. Faça login novamente para continuar.");
      } else {
        setError(
          err?.response?.data?.message || "Erro ao gerar pagamento PIX."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPix = useCallback(async () => {
    if (!pixData?.pixQrCode) return;
    try {
      await navigator.clipboard.writeText(pixData.pixQrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback para browsers sem Clipboard API
      try {
        const textarea = document.createElement("textarea");
        textarea.value = pixData.pixQrCode;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setError("Não foi possível copiar. Copie manualmente o código abaixo do QR Code.");
      }
    }
  }, [pixData]);

  const selectedPricing = fedInfo?.pricingTables.find((p) => p.id === pricingTableId);

  // --- STEP: FORM (Renewal mode) ---
  if (step === "form" && isRenewal && existingAffiliationId) {
    // In renewal mode, show a simplified form: just plan selection + renew button
    const renewalEligiblePlans = fedInfo?.pricingTables.filter((p) => {
      // Show CREDENTIAL_RENEWAL plans first, then fallback to affiliation plans
      return p.category === "CREDENTIAL_RENEWAL" ||
        checkPlanEligibility(p, athleteBirthDate, athleteBelt, isMinorByBirthDate(athleteBirthDate));
    }) || [];

    // Sort: CREDENTIAL_RENEWAL first
    const sortedPlans = [...renewalEligiblePlans].sort((a, b) => {
      if (a.category === "CREDENTIAL_RENEWAL" && b.category !== "CREDENTIAL_RENEWAL") return -1;
      if (a.category !== "CREDENTIAL_RENEWAL" && b.category === "CREDENTIAL_RENEWAL") return 1;
      return 0;
    });

    return (
      <Container>
        <Title>
          <AutorenewIcon sx={{ fontSize: 18, color: "var(--navy, #1a2252)" }} />
          Renovar filiação {federationName ? `- ${federationName}` : ""}
        </Title>
        <Subtitle>
          Sua filiação expirou. Selecione um plano para renovar.
        </Subtitle>

        {error && (
          <ResultBox $variant="error" style={{ marginTop: 0, marginBottom: 12 }}>
            <ResultContent>
              <ResultDetail>{error}</ResultDetail>
            </ResultContent>
          </ResultBox>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Plan selection */}
          {sortedPlans.length > 0 && (
            <Box>
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "var(--navy, #1a2252)" }}
              >
                Plano de renovação
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {sortedPlans.map((plan) => (
                  <Box
                    key={plan.id}
                    onClick={() => setPricingTableId(plan.id)}
                    sx={{
                      flex: "1 1 180px",
                      maxWidth: 280,
                      border: pricingTableId === plan.id
                        ? "2px solid var(--navy, #1a2252)"
                        : "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      cursor: "pointer",
                      bgcolor: pricingTableId === plan.id ? "#f0f4ff" : "#fff",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "var(--navy, #1a2252)" },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {plan.name}
                    </Typography>
                    {plan.eligibility?.description && (
                      <Typography sx={{ fontSize: 11, color: "#6b7490", mt: 0.5 }}>
                        {plan.eligibility.description}
                      </Typography>
                    )}
                    <Typography
                      sx={{ fontSize: 18, fontWeight: 700, color: "var(--navy, #1a2252)", mt: 0.5 }}
                      translate="no"
                    >
                      {formatCurrency(plan.priceCents)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Loading federation info */}
          {!fedInfo && !error && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: 13, color: "#6b7490" }}>
                Carregando dados da federação...
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleRenewAffiliation}
            disabled={loading || !fedInfo || !pricingTableId}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              height: 44,
              bgcolor: "var(--navy, #1a2252)",
              "&:hover": { bgcolor: "var(--navy-light, #243070)" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : selectedPricing ? (
              `Renovar por ${formatCurrency(selectedPricing.priceCents)}`
            ) : (
              "Renovar filiação"
            )}
          </Button>
        </Box>
      </Container>
    );
  }

  // --- STEP: FORM ---
  if (step === "form") {
    return (
      <Container>
        <Title>
          <PersonAddIcon sx={{ fontSize: 18, color: "var(--navy, #1a2252)" }} />
          Filiar-se {federationName ? `- ${federationName}` : ""}
        </Title>
        <Subtitle>
          Este torneio exige filiação ativa. Preencha os dados abaixo para se filiar.
        </Subtitle>

        {error && (
          <ResultBox $variant="error" style={{ marginTop: 0, marginBottom: 12 }}>
            <ResultContent>
              <ResultDetail>{error}</ResultDetail>
            </ResultContent>
          </ResultBox>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Dados pré-preenchidos do atleta */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Nome"
              value={athleteName}
              size="small"
              disabled
              sx={{ flex: 1, minWidth: 200 }}
            />
            <TextField
              label="CPF do Atleta *"
              value={cpfInput}
              onChange={(e) => setCpfInput(e.target.value)}
              size="small"
              disabled={!!athleteCpf}
              placeholder="000.000.000-00"
              sx={{ flex: 1, minWidth: 150 }}
              inputProps={{ maxLength: 14 }}
              helperText={!athleteCpf ? "Obrigatorio para a filiacao" : ""}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="E-mail"
              value={athleteEmail}
              size="small"
              disabled
              sx={{ flex: 1, minWidth: 200 }}
            />
            {athletePhone && (
              <TextField
                label="Telefone"
                value={athletePhone}
                size="small"
                disabled
                sx={{ flex: 1, minWidth: 150 }}
              />
            )}
          </Box>


          {/* Dados do responsável (quando atleta é menor ou dependente) */}
          {(athleteIsMinor || isDependentAthlete) && accountName && (
            <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: "var(--navy, #1a2252)" }}>
                Dados do Responsável
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#666", mb: 1.5 }}>
                {athleteIsMinor
                  ? "Como o atleta e menor de idade, os dados do responsavel sao obrigatorios."
                  : "Como voce esta inscrevendo outro atleta, seus dados serao vinculados como responsavel."}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Nome do Responsável"
                  value={accountName}
                  size="small"
                  disabled
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label="CPF do Responsável *"
                  value={ownerCpfInput}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
                    let formatted = digits;
                    if (digits.length > 3) formatted = `${digits.slice(0, 3)}.${digits.slice(3)}`;
                    if (digits.length > 6) formatted = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
                    if (digits.length > 9) formatted = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
                    setOwnerCpfInput(formatted);
                  }}
                  size="small"
                  disabled={!!(accountCpf && accountCpf.replace(/\D/g, "").length === 11)}
                  placeholder="000.000.000-00"
                  sx={{ flex: 1, minWidth: 150 }}
                  inputProps={{ maxLength: 14 }}
                  helperText={!(accountCpf && accountCpf.replace(/\D/g, "").length === 11) ? "Obrigatorio" : ""}
                />
              </Box>
              {(accountPhone || accountEmail) && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1.5 }}>
                  {accountEmail && (
                    <TextField
                      label="E-mail do Responsável"
                      value={accountEmail}
                      size="small"
                      disabled
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                  )}
                  {accountPhone && (
                    <TextField
                      label="Telefone do Responsável"
                      value={accountPhone}
                      size="small"
                      disabled
                      sx={{ flex: 1, minWidth: 150 }}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Info de acesso ao painel da federação */}
          <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: "var(--navy, #1a2252)" }}>
              Acesso ao painel da federacao
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#388e3c" }}>
              Apos a filiacao, voce podera acessar o painel da federacao usando o mesmo e-mail (<strong>{(athleteIsMinor || isDependentAthlete) ? accountEmail || athleteEmail : athleteEmail}</strong>) e a mesma senha do Torneio Facil.
            </Typography>
          </Box>

          {/* Seleção de academia */}
          {fedInfo && fedInfo.academies.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>Academia</InputLabel>
              <Select
                value={academyId}
                label="Academia"
                onChange={(e) => setAcademyId(e.target.value)}
              >
                {fedInfo.academies.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}{a.city ? ` - ${a.city}` : ""}{a.state ? `/${a.state}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Campos dinâmicos da federação */}
          {fedInfo?.formConfig && (() => {
            const dynFields = [
              ...(fedInfo.formConfig.extraBaseFields || []),
              ...(fedInfo.formConfig.customFields || []),
            ];
            if (dynFields.length === 0) return null;

            // Separar campos de upload dos demais
            const isFileField = (f: FormConfigField) =>
              f.key === "photoUrl" || f.key === "documentUrl" ||
              f.type === "file";
            const fileFields = dynFields.filter(isFileField);
            const otherFields = dynFields.filter((f) => !isFileField(f));

            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Uploads lado a lado */}
                {fileFields.length > 0 && (
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {fileFields.map((field) => {
                      if (field.key === "photoUrl" || (field.type === "file" && field.label.toLowerCase().includes("foto"))) {
                        return (
                          <Box key={field.key} sx={{ flex: "1 1 200px", minWidth: 200 }}>
                            <PhotoUpload
                              value={photoUrl}
                              onChange={setPhotoUrl}
                              required={field.required}
                              label={field.label}
                            />
                          </Box>
                        );
                      }
                      if (field.key === "documentUrl" || (field.type === "file" && field.label.toLowerCase().includes("documento"))) {
                        return (
                          <Box key={field.key} sx={{ flex: "1 1 200px", minWidth: 200 }}>
                            <DocumentUpload
                              value={documentUrl}
                              onChange={setDocumentUrl}
                              required={field.required}
                              label={field.label}
                            />
                          </Box>
                        );
                      }
                      return (
                        <Box key={field.key} sx={{ flex: "1 1 200px", minWidth: 200 }}>
                          <DocumentUpload
                            value={(customFieldValues[field.key] as string) || ""}
                            onChange={(v) =>
                              setCustomFieldValues((prev) => ({ ...prev, [field.key]: v }))
                            }
                            required={field.required}
                            label={field.label}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {otherFields.map((field) => {
                  if (field.type === "select" && field.options) {
                    return (
                      <FormControl key={field.key} size="small" fullWidth required={field.required}>
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                          value={customFieldValues[field.key] || ""}
                          label={field.label}
                          onChange={(e) =>
                            setCustomFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                          }
                        >
                          {field.options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }
                  if (field.type === "checkbox") {
                    return (
                      <FormControlLabel
                        key={field.key}
                        control={
                          <Checkbox
                            checked={!!customFieldValues[field.key]}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [field.key]: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={`${field.label}${field.required ? " *" : ""}`}
                      />
                    );
                  }
                  if (field.type === "textarea") {
                    return (
                      <TextField
                        key={field.key}
                        label={field.label}
                        value={customFieldValues[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        placeholder={field.placeholder}
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        required={field.required}
                      />
                    );
                  }
                  // text, number, date
                  return (
                    <TextField
                      key={field.key}
                      label={field.label}
                      type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                      value={customFieldValues[field.key] || ""}
                      onChange={(e) =>
                        setCustomFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      placeholder={field.placeholder}
                      size="small"
                      fullWidth
                      required={field.required}
                      InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                    />
                  );
                })}
              </Box>
            );
          })()}

          {/* Seleção de plano — filtrado por elegibilidade */}
          {fedInfo && fedInfo.pricingTables.length > 0 && (() => {
            // Obter faixa dos campos customizados se disponível
            const beltFromCustom = (customFieldValues?.faixa ?? customFieldValues?.belt ?? athleteBelt ?? undefined) as string | undefined;
            const eligiblePlans = fedInfo.pricingTables.filter((plan) =>
              plan.category !== "CREDENTIAL_RENEWAL" &&
              checkPlanEligibility(plan, athleteBirthDate, beltFromCustom, athleteIsMinor)
            );
            if (eligiblePlans.length === 0) return null;
            return (
              <Box>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "var(--navy, #1a2252)" }}
                >
                  Plano de filiação
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {eligiblePlans.map((plan) => (
                    <Box
                      key={plan.id}
                      onClick={() => setPricingTableId(plan.id)}
                      sx={{
                        flex: "1 1 180px",
                        maxWidth: 280,
                        border: pricingTableId === plan.id
                          ? "2px solid var(--navy, #1a2252)"
                          : "1px solid #e2e8f0",
                        borderRadius: "8px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        bgcolor: pricingTableId === plan.id ? "#f0f4ff" : "#fff",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: "var(--navy, #1a2252)" },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {plan.name}
                      </Typography>
                      {plan.eligibility?.description && (
                        <Typography sx={{ fontSize: 11, color: "#6b7490", mt: 0.5 }}>
                          {plan.eligibility.description}
                        </Typography>
                      )}
                      <Typography
                        sx={{ fontSize: 18, fontWeight: 700, color: "var(--navy, #1a2252)", mt: 0.5 }}
                        translate="no"
                      >
                        {formatCurrency(plan.priceCents)}
                      </Typography>
                      {plan.isRecurring && (
                        <Typography sx={{ fontSize: 11, color: "#6b7490" }}>
                          Renovação automática
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}

          {/* Loading de info da federação */}
          {!fedInfo && !error && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: 13, color: "#6b7490" }}>
                Carregando dados da federação...
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleCreateAffiliation}
            disabled={loading || !fedInfo || !pricingTableId}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              height: 44,
              bgcolor: "var(--navy, #1a2252)",
              "&:hover": { bgcolor: "var(--navy-light, #243070)" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : selectedPricing ? (
              `Filiar-se por ${formatCurrency(selectedPricing.priceCents)}`
            ) : (
              "Filiar-se"
            )}
          </Button>
        </Box>
      </Container>
    );
  }

  // --- STEP: PAYMENT (PIX) ---
  if (step === "payment") {
    return (
      <Container>
        <Title>
          <QrCode2Icon sx={{ fontSize: 18, color: "var(--navy, #1a2252)" }} />
          Pagamento da filiação
        </Title>
        <Subtitle>
          Escaneie o QR Code com o app do seu banco para pagar via PIX.
        </Subtitle>

        {error && (
          <ResultBox $variant="error" style={{ marginTop: 0, marginBottom: 12 }}>
            <ResultContent>
              <ResultDetail>{error}</ResultDetail>
            </ResultContent>
          </ResultBox>
        )}

        {pixData ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            {/* QR Code Image */}
            {pixData.pixQrCodeImage && (
              <Box
                component="img"
                src={
                  pixData.pixQrCodeImage.startsWith("data:")
                    ? pixData.pixQrCodeImage
                    : pixData.pixQrCodeImage.startsWith("http")
                      ? pixData.pixQrCodeImage
                      : `data:image/png;base64,${pixData.pixQrCodeImage}`
                }
                alt="QR Code PIX"
                sx={{
                  width: 220,
                  height: 220,
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              />
            )}

            {/* Valor */}
            <Typography
              sx={{ fontSize: 20, fontWeight: 700, color: "var(--navy, #1a2252)" }}
              translate="no"
            >
              {formatCurrency(pixData.amount)}
            </Typography>

            {/* Copia e cola */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyPix}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: copied ? "#16a34a" : "var(--navy, #1a2252)",
                borderColor: copied ? "#16a34a" : "var(--navy, #1a2252)",
              }}
            >
              {copied ? "Copiado!" : "Copiar código PIX"}
            </Button>

            {/* Temporizador */}
            {pixData.pixExpiresAt && (
              pixExpired ? (
                <Typography sx={{ fontSize: 13, color: "#dc2626", fontWeight: 600 }}>
                  QR Code expirado
                </Typography>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <HourglassTopIcon sx={{ fontSize: 16, color: "#6b7490" }} />
                  <Typography sx={{ fontSize: 13, color: "#6b7490" }}>
                    Expira em{" "}
                    <span translate="no" style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 15 }}>
                      {pixCountdown}
                    </span>
                  </Typography>
                </Box>
              )
            )}

            {/* PIX expirado — botão para gerar novo (com limite) */}
            {pixExpired ? (
              pixRegenerationExhausted ? (
                <ResultBox $variant="error" style={{ marginTop: 8 }}>
                  <ResultContent>
                    <ResultTitle>Limite de tentativas atingido</ResultTitle>
                    <ResultDetail>
                      Não foi possível concluir o pagamento após várias tentativas.
                      Entre em contato com a {federationName} para assistência.
                    </ResultDetail>
                  </ResultContent>
                </ResultBox>
              ) : (
                <>
                  <ResultBox $variant="error" style={{ marginTop: 8 }}>
                    <ResultContent>
                      <ResultDetail>
                        O QR Code PIX expirou. Gere um novo para continuar.
                        {pixRegenerateCountRef.current > 0 && (
                          <> ({MAX_PIX_REGENERATIONS - pixRegenerateCountRef.current} tentativa{MAX_PIX_REGENERATIONS - pixRegenerateCountRef.current !== 1 ? "s" : ""} restante{MAX_PIX_REGENERATIONS - pixRegenerateCountRef.current !== 1 ? "s" : ""})</>
                        )}
                      </ResultDetail>
                    </ResultContent>
                  </ResultBox>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setPixExpired(false);
                      setPixData(null);
                      handleGeneratePix();
                    }}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: "var(--navy, #1a2252)",
                      "&:hover": { bgcolor: "var(--navy-light, #243070)" },
                    }}
                  >
                    {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Gerar novo QR Code"}
                  </Button>
                </>
              )
            ) : pollingStopped ? (
              /* Polling parou por erros — botão para retomar */
              <>
                <ResultBox $variant="error" style={{ marginTop: 8 }}>
                  <ResultContent>
                    <ResultDetail>
                      Não foi possível verificar o status do pagamento.
                    </ResultDetail>
                  </ResultContent>
                </ResultBox>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={restartPolling}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: "var(--navy, #1a2252)",
                    borderColor: "var(--navy, #1a2252)",
                  }}
                >
                  Tentar novamente
                </Button>
              </>
            ) : (
              /* Status de polling normal */
              <ResultBox $variant="info">
                <CircularProgress size={16} sx={{ color: "#1e40af", mt: "3px" }} />
                <ResultContent>
                  <ResultDetail>
                    Aguardando confirmacao do pagamento...
                  </ResultDetail>
                </ResultContent>
              </ResultBox>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
            {loading ? (
              <>
                <CircularProgress size={24} />
                <Typography sx={{ fontSize: 13, color: "#6b7490" }}>
                  Gerando QR Code PIX...
                </Typography>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleGeneratePix()}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "var(--navy, #1a2252)",
                  "&:hover": { bgcolor: "var(--navy-light, #243070)" },
                }}
              >
                Gerar QR Code PIX
              </Button>
            )}
          </Box>
        )}
      </Container>
    );
  }

  // --- STEP: PENDING ---
  if (step === "pending") {
    return (
      <Container>
        <Title>
          <HourglassTopIcon sx={{ fontSize: 18, color: "#d97706" }} />
          Filiação em análise
        </Title>

        {error && (
          <ResultBox $variant="error" style={{ marginTop: 0, marginBottom: 12 }}>
            <ResultContent>
              <ResultDetail>{error}</ResultDetail>
            </ResultContent>
          </ResultBox>
        )}

        <ResultBox $variant="warning">
          <HourglassTopIcon sx={{ fontSize: 22, color: "#d97706", mt: "2px" }} />
          <ResultContent>
            <ResultTitle>Aguardando aprovação da federação</ResultTitle>
            <ResultDetail>
              Seu pagamento foi confirmado. A federação irá analisar e aprovar sua filiação.
              Você será notificado quando a filiação estiver ativa.
            </ResultDetail>
            {pollingStopped ? (
              <Button
                variant="outlined"
                size="small"
                onClick={restartPolling}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#92400e",
                  borderColor: "#d97706",
                }}
              >
                Tentar novamente
              </Button>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                <CircularProgress size={14} sx={{ color: "#d97706" }} />
                <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                  Verificando status automaticamente...
                </Typography>
              </Box>
            )}
          </ResultContent>
        </ResultBox>
      </Container>
    );
  }

  // --- STEP: ACTIVE ---
  if (step === "active") {
    return (
      <Container>
        <ResultBox $variant="success">
          <CheckCircleIcon sx={{ fontSize: 22, color: "#16a34a", mt: "2px" }} />
          <ResultContent>
            <ResultTitle>Filiação aprovada!</ResultTitle>
            <ResultDetail>
              Sua filiação na {federationName} foi aprovada. Você já pode prosseguir com a inscrição.
            </ResultDetail>
          </ResultContent>
        </ResultBox>
      </Container>
    );
  }

  return null;
};

export default FederationAffiliationBox;
